import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Check, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ContributorCategoryMapToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { useContributorCategoryMap, useContributorCategoryMapMutations } from '~/hooks/use-contributor-subviews';
import { useContributorCategoriesList } from '~/hooks/use-contributor-categories';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contributorId: number | null;
	contributorName?: string;
}

const EMPTY: ContributorCategoryMapToken = {
	smry: '',
	bio: '',
	jobtl: '',
	eml: '',
	tel: '',
	mob: '',
	www: '',
	fb: '',
	instgm: '',
	isFeatured: false,
	iv: true,
	si: 0
};

/** Per-contributor category map sub-grid (legacy ContributorsCategoryMap popup). List + inline add/edit. */
export function ContributorCategoryMapDialog({ open, onOpenChange, contributorId, contributorName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useContributorCategoryMap(open ? contributorId : null);
	const { create, update, remove } = useContributorCategoryMapMutations(contributorId ?? 0);
	const { data: categories } = useContributorCategoriesList();
	const [editing, setEditing] = useState<ContributorCategoryMapToken | null>(null);

	useEffect(() => {
		if (!open) setEditing(null);
	}, [open]);

	const set = (patch: Partial<ContributorCategoryMapToken>) => setEditing(prev => ({ ...(prev ?? EMPTY), ...patch }));

	const save = async () => {
		if (!editing) return;
		if (!editing.catId) {
			toast.error(t('validation.required'));
			return;
		}
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const del = (m: ContributorCategoryMapToken) => {
		if (!m.id) return;
		remove.mutate(m.id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('contributors.list.subviews.categoryMap.title')}</DialogTitle>
					<DialogDescription>{contributorName}</DialogDescription>
				</DialogHeader>

				{editing ? (
					<div className="flex max-h-[70vh] flex-col gap-3 overflow-auto">
						<div className="grid grid-cols-2 gap-3">
							<FormField label={t('contributors.list.subviews.categoryMap.category')}>
								<Select
									value={editing.catId != null && editing.catId > 0 ? String(editing.catId) : ''}
									onValueChange={v => set({ catId: Number(v) })}>
									<SelectTrigger className="w-full" aria-label={t('contributors.list.subviews.categoryMap.category')}>
										<SelectValue placeholder="—" />
									</SelectTrigger>
									<SelectContent>
										{(categories ?? [])
											.filter(c => c.id != null)
											.map(c => (
												<SelectItem key={c.id} value={String(c.id)}>
													{c.nm}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</FormField>
							<FormField label={t('contributors.list.subviews.categoryMap.jobTitle')}>
								<Input value={editing.jobtl ?? ''} onChange={e => set({ jobtl: e.target.value })} maxLength={50} />
							</FormField>
						</div>
						<FormField label={t('contributors.list.subviews.categoryMap.summary')}>
							<Input value={editing.smry ?? ''} onChange={e => set({ smry: e.target.value })} maxLength={200} />
						</FormField>
						<FormField label={t('contributors.list.subviews.categoryMap.bio')}>
							<Textarea value={editing.bio ?? ''} onChange={e => set({ bio: e.target.value })} rows={3} />
						</FormField>
						<div className="grid grid-cols-3 gap-3">
							<FormField label={t('contributors.list.subviews.categoryMap.email')}>
								<Input value={editing.eml ?? ''} onChange={e => set({ eml: e.target.value })} maxLength={255} />
							</FormField>
							<FormField label={t('contributors.list.subviews.categoryMap.tel')}>
								<Input value={editing.tel ?? ''} onChange={e => set({ tel: e.target.value })} maxLength={20} />
							</FormField>
							<FormField label={t('contributors.list.subviews.categoryMap.mobile')}>
								<Input value={editing.mob ?? ''} onChange={e => set({ mob: e.target.value })} maxLength={20} />
							</FormField>
							<FormField label={t('contributors.list.subviews.categoryMap.website')}>
								<Input value={editing.www ?? ''} onChange={e => set({ www: e.target.value })} maxLength={200} />
							</FormField>
							<FormField label="Facebook">
								<Input value={editing.fb ?? ''} onChange={e => set({ fb: e.target.value })} maxLength={200} />
							</FormField>
							<FormField label="Instagram">
								<Input value={editing.instgm ?? ''} onChange={e => set({ instgm: e.target.value })} maxLength={200} />
							</FormField>
						</div>
						<div className="grid grid-cols-3 gap-3">
							<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor="cm-feat">{t('contributors.list.subviews.categoryMap.featured')}</Label>
								<Switch id="cm-feat" checked={Boolean(editing.isFeatured)} onCheckedChange={next => set({ isFeatured: next })} />
							</div>
							<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor="cm-iv">{t('contributors.list.subviews.categoryMap.visible')}</Label>
								<Switch id="cm-iv" checked={Boolean(editing.iv)} onCheckedChange={next => set({ iv: next })} />
							</div>
							<FormField label={t('contributors.list.subviews.categoryMap.sort')}>
								<Input
									type="number"
									value={editing.si ?? 0}
									onChange={e => set({ si: e.target.value === '' ? 0 : Number(e.target.value) })}
								/>
							</FormField>
						</div>
						<div className="flex justify-between">
							<Button variant="ghost" onClick={() => setEditing(null)}>
								<ArrowLeft size={15} /> {t('common.cancel')}
							</Button>
							<Button onClick={save} disabled={create.isPending || update.isPending}>
								{t('common.save')}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex justify-end">
							<Button size="sm" onClick={() => setEditing({ ...EMPTY })}>
								<Plus size={15} /> {t('contributors.list.subviews.categoryMap.add')}
							</Button>
						</div>
						<div className="max-h-[55vh] overflow-auto rounded-md border border-border">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-3">
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton key={i} className="h-9 w-full rounded-md" />
									))}
								</div>
							) : !data || data.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<Link2 size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.categoryMap.category')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('contributors.list.subviews.categoryMap.jobTitle')}</th>
											<th className="px-3 py-2 text-center font-medium">{t('contributors.list.subviews.categoryMap.featured')}</th>
											<th className="px-3 py-2 text-center font-medium">{t('contributors.list.subviews.categoryMap.visible')}</th>
											<th className="px-2 py-2" style={{ width: 80 }} />
										</tr>
									</thead>
									<tbody>
										{data.map(m => (
											<tr key={m.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2">
													<span className="inline-flex items-center gap-2 text-foreground">
														{m.chex && (
															<span
																className="inline-block h-3 w-3 rounded-full border border-border"
																style={{ backgroundColor: m.chex }}
															/>
														)}
														{m.contributorCategoryName || '—'}
													</span>
												</td>
												<td className="px-3 py-2 text-foreground-muted">{m.jobtl || '—'}</td>
												<td className="px-3 py-2 text-center">
													{m.isFeatured ? (
														<Check size={16} className="mx-auto text-success-fg" />
													) : (
														<span className="text-foreground-disabled">—</span>
													)}
												</td>
												<td className="px-3 py-2 text-center">
													{m.iv ? (
														<Check size={16} className="mx-auto text-success-fg" />
													) : (
														<span className="text-foreground-disabled">—</span>
													)}
												</td>
												<td className="px-2 py-1 text-center whitespace-nowrap">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label={t('common.edit')}
														onClick={() => setEditing(m)}>
														<Pencil size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-danger-fg"
														aria-label={t('common.delete')}
														onClick={() => del(m)}>
														<Trash2 size={14} />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
